import { useState } from 'react';
import { Link } from 'react-router-dom';

import { StateMessage } from '@/components/ui/controls';
import { Badge, Card, Field } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import { useIdentity } from '@/features/identity/identity';
import type { TelegramChat } from '@/types/api';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

import type { BotText, GroupCheck, TelegramMode, TelegramSettings } from './api-types';
import SettingsHeader from './settings-nav';

const MODES: Record<TelegramMode, { label: string; hint: string }> = {
  off: { label: 'Off', hint: 'No bot.' },
  simulate: { label: 'Simulate', hint: 'No Telegram at all: replies are only recorded. For local work.' },
  polling: { label: 'Polling', hint: 'Prisma asks Telegram for updates. For local work with a real test bot.' },
  webhook: { label: 'Webhook', hint: 'Telegram calls Prisma. How it runs when deployed.' },
};

const failure = (err: unknown, invalid: string) =>
  (err as { response?: { status?: number } }).response?.status === 422 ? invalid : 'That did not work. Try again.';

/** One thing the bot says: what it is for, and the text, which an admin can change or put back. */
const TextEditor = ({ text, canEdit, onSaved }: { text: BotText; canEdit: boolean; onSaved: () => void }) => {
  const [draft, setDraft] = useState(text.text);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const keep = text.fills.length ? `Keep ${text.fills.length === 1 ? 'the %s' : `the ${text.fills.length} %s`}: ${text.fills.join(', ')}, in that order.` : '';

  const call = async (request: () => Promise<{ data: BotText }>) => {
    setBusy(true);
    setProblem(null);
    try {
      const r = await request();
      setDraft(r.data.text);
      onSaved();
    } catch (err) {
      setProblem(failure(err, keep || `Write something, up to ${text.max.toLocaleString('en')} characters.`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li>
      <p className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        {text.when}
        <span className="pill rounded-pill px-2 py-px font-mono text-[11px] text-fg-muted">{text.requirement}</span>
        {text.custom && <Badge tone="brand">Changed</Badge>}
      </p>
      {canEdit ? (
        <>
          <textarea
            rows={3}
            maxLength={text.max}
            className={`${field.textarea} mt-1.5`}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setProblem(null);
            }}
            aria-label={text.when}
          />
          {keep && <p className="mt-1 text-xs text-fg-faint">{keep}</p>}
          {problem && <p className="mt-1 text-xs text-danger">{problem}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className={btn.secondary}
              disabled={busy || draft.trim() === text.text}
              onClick={() => call(() => http.put<BotText>(`/settings/telegram/texts/${text.key}`, { text: draft }))}
            >
              Save
            </button>
            {text.custom && (
              <button type="button" className={btn.quiet} disabled={busy} onClick={() => call(() => http.delete<BotText>(`/settings/telegram/texts/${text.key}`))}>
                Back to the default
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="mt-1 whitespace-pre-wrap break-words rounded-lg bg-info/15 px-3 py-2 text-sm text-fg">{text.text}</p>
      )}
    </li>
  );
};

/** Asks Telegram whether the bot is still in each group Prisma knows, and counts their members. */
const GroupChecker = ({ onChecked }: { onChecked: () => void }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GroupCheck | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const check = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const r = await http.post<GroupCheck>('/settings/telegram/check-groups');
      setResult(r.data);
      onChecked();
    } catch (err) {
      setProblem(failure(err, 'That did not work. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
  return (
    <div className="flex flex-col gap-2">
      <button type="button" className={`${btn.secondary} self-start`} disabled={busy} onClick={check}>
        {busy ? 'Checking…' : 'Check groups now'}
      </button>
      {result && (
        <p className="text-sm">
          {result.checked === 0 ? (
            'Prisma knows no group the bot is in.'
          ) : (
            <>
              Checked {plural(result.checked, 'group')}: the bot is in {result.in}.
              {result.left.length > 0 && ` It is no longer in ${result.left.join(', ')}, now marked as left.`}
              {result.failed > 0 && <span className="text-danger"> Telegram did not answer for {plural(result.failed, 'group')}; try again later.</span>}
            </>
          )}
        </p>
      )}
      {problem && <p className="text-xs text-danger">{problem}</p>}
    </div>
  );
};

/** The team's own group: chosen from the groups the bot is in. */
const TeamChatPicker = ({ current, onSaved }: { current: TelegramSettings['team_chat']; onSaved: () => void }) => {
  const chats = useApi<TelegramChat[]>('/telegram/chats');
  // A client's group (linked to a lead, or one where an email was given) is never the team's.
  // The team's own group is not in the list, so it is added as it is.
  const groups = (chats.data ?? []).filter((c) => c.kind === 'group' && !c.left_at && !c.lead_id && !c.claimed_email);
  const [choice, setChoice] = useState(current.source === 'app' ? String(current.id) : '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await http.put('/settings/telegram/team-chat', { chat_id: Number(choice) || 0 });
      onSaved();
    } catch (err) {
      setProblem(failure(err, 'Pick a group the bot is still in.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
      <select className={`${field.select} w-full sm:w-auto`} value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="The team's group">
        <option value="">{current.source === 'config' ? 'The one in the config' : 'None'}</option>
        {current.source === 'app' && <option value={String(current.id)}>{current.title || String(current.id)}</option>}
        {groups.map((c) => (
          <option key={c.chat_id} value={c.chat_id}>
            {c.title}
          </option>
        ))}
      </select>
      <button type="button" className={btn.secondary} disabled={busy || choice === (current.source === 'app' ? String(current.id) : '')} onClick={save}>
        Save
      </button>
      {problem && <p className="text-xs text-danger">{problem}</p>}
    </div>
  );
};

/**
 * Telegram bot settings (TG-6, REM-1, ATT-2). The team, its group and what the bot says
 * are set here, by an admin (anyone while sign-in is in local mode); the team comes from
 * the Telegram usernames on the Staff page. How the bot runs (mode, username, token)
 * stays in the backend config.
 */
const TelegramSettingsPage = () => {
  const { me, oidc } = useIdentity();
  const canEdit = !oidc || me?.role === 'admin';
  const { data: bot, error, reload } = useApi<TelegramSettings>('/settings/telegram');

  if (!bot) {
    return (
      <div className="max-w-4xl">
        <SettingsHeader />
        {error ? (
          <StateMessage title="The bot settings could not be loaded" tone="error">
            {error}
          </StateMessage>
        ) : (
          <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
        )}
      </div>
    );
  }

  const mode = MODES[bot.mode] ?? { label: bot.mode, hint: '' };
  const chat = bot.team_chat;
  return (
    <div className="max-w-4xl">
      <SettingsHeader />
      <p className="-mt-2 mb-6 break-words text-sm text-fg-muted">
        {canEdit ? 'The team, its group and what the bot says are set here.' : 'Only admins can change these.'} How the bot runs comes from the backend config
        (prisma-telegram-*).
      </p>

      <div className="space-y-4">
        <Card title="Bot">
          <dl>
            <Field label="Mode">
              <Badge tone={bot.mode === 'off' ? 'neutral' : 'info'}>{mode.label}</Badge> <span className="text-fg-muted">{mode.hint}</span>
            </Field>
            {bot.bot_name && <Field label="Name">{bot.bot_name}</Field>}
            <Field label="Username">@{bot.bot_username}</Field>
            <Field label="Webhook">
              {bot.mode !== 'webhook' ? (
                <span className="text-fg-muted">Not used in this mode</span>
              ) : bot.webhook_url_set ? (
                'Registered with Telegram at start-up'
              ) : (
                <span className="text-fg-muted">No URL set: register the webhook with Telegram by hand</span>
              )}
            </Field>
            <Field label="Joins groups">
              {bot.joins_groups === null ? (
                <span className="text-fg-muted">Not checked: no real bot runs in this mode. It must be allowed in BotFather (/setjoingroups).</span>
              ) : bot.joins_groups ? (
                'Yes'
              ) : (
                <span className="text-danger">No: nobody can add it to a group. Turn on Allow Groups in BotFather (/setjoingroups).</span>
              )}
            </Field>
            <Field label="Privacy mode">
              {bot.reads_all_messages === null ? (
                <span className="text-fg-muted">Not checked: no real bot runs in this mode. It must be off, so the bot sees whole conversations (TG-3).</span>
              ) : bot.reads_all_messages ? (
                'Off: it sees whole conversations'
              ) : (
                <span className="text-danger">
                  On: in groups it sees only commands and mentions. Turn it off in BotFather (/setprivacy), then remove the bot from each group and add it again.
                </span>
              )}
            </Field>
            {canEdit && bot.joins_groups !== null && (
              <Field label="Its groups">
                <GroupChecker onChecked={reload} />
                <p className="mt-1 text-xs text-fg-muted">
                  Telegram cannot list a bot&apos;s groups, so this asks about the ones Prisma knows: whether the bot is still in them, and how many members they have.
                </p>
              </Field>
            )}
            {bot.add_to_group_url && (
              <Field label="Add to a group">
                <a href={bot.add_to_group_url} target="_blank" rel="noreferrer" className={`${btn.link} inline-flex min-h-11 items-center md:min-h-0`}>
                  Open in Telegram
                </a>{' '}
                <span className="text-fg-muted">To link the group to a lead at once, use Add the bot to their group on the lead.</span>
              </Field>
            )}
          </dl>
        </Card>

        <Card title="Team">
          {bot.team.length ? (
            <span className="flex flex-wrap gap-1.5">
              {bot.team.map((m) => (
                <span key={m.username} className="pill rounded-pill px-2.5 py-0.5 text-xs text-fg" title={m.source === 'config' ? 'From the backend config' : undefined}>
                  @{m.username}
                  {m.name ? <span className="text-fg-muted"> · {m.name}</span> : <span className="text-fg-faint"> · config</span>}
                </span>
              ))}
            </span>
          ) : (
            <p className="text-sm text-fg-muted">Nobody yet.</p>
          )}
          <p className="mt-3 text-xs text-fg-faint">
            Their messages in a client group count as the team&rsquo;s. Add someone&rsquo;s Telegram username on the{' '}
            <Link to="/settings/staff" className={btn.link}>
              Staff page
            </Link>
            .
          </p>
        </Card>

        <Card title="Reminders">
          <p className="text-sm text-fg-muted">Follow-ups that are due (REM-1), and clients who have waited 2 days for an answer from a person (Q21).</p>
          <dl className="mt-2">
            <Field label="By direct message">
              {bot.reminder_dms.length ? (
                bot.reminder_dms.join(', ')
              ) : (
                <span className="text-fg-muted">Nobody yet. A team member writes to @{bot.bot_username} once, from the Telegram username on the Staff page.</span>
              )}
            </Field>
            <Field label="Team chat">
              {chat.id ? (
                <>
                  {chat.title || <span className="font-mono text-xs">{chat.id}</span>}{' '}
                  <span className="text-fg-muted">{chat.source === 'app' ? '· set here' : '· from the config'}</span>
                </>
              ) : (
                <span className="text-fg-muted">None</span>
              )}
            </Field>
          </dl>
          {canEdit && <TeamChatPicker key={`${chat.source}:${chat.id}`} current={chat} onSaved={reload} />}
          <p className="mt-3 text-xs text-fg-faint">
            The team chat gets the reminders only while nobody gets them by direct message. It is never treated as a client conversation. Add the bot to the group first.
          </p>
        </Card>

        <Card title="What the bot says">
          <ul className="space-y-5">
            {bot.texts.map((t) => (
              <TextEditor key={`${t.key}:${t.text}`} text={t} canEdit={canEdit} onSaved={reload} />
            ))}
          </ul>
          <p className="mt-4 break-words text-xs text-fg-faint">
            Each %s is filled in when the message is sent, and {'{{botname}}'} becomes the bot&apos;s name in Telegram{bot.bot_name && ` (${bot.bot_name})`}. The
            defaults are in backend/pkg/api/telegram.go. Clients get English (Q11).
          </p>
        </Card>
      </div>
    </div>
  );
};

export default TelegramSettingsPage;
